import axios from 'axios';
import axiosConfig from '../../../../../../redux/Project/axiosConfig';

export interface BuckerarrestorS3UploadOptions {
  files: File[];
  projectId: number;
  /** Weld folder id — S3: projects/.../manual_weld_data/{weldId}/{file}.txt */
  weldId: string;
}

export interface BuckerarrestorS3UploadedFile {
  s3Key: string;
  fileName: string;
}

/** Server batch max for getManualWeldDataPreSignedUrlToUpload. */
const PRESIGN_BATCH_SIZE = 100;
/** Parallel PUTs within the all-files batch. */
const PUT_CONCURRENCY = 8;

async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * Batch upload ALL files to S3 under project/manual_weld_data/{weldId}/ (no pipe / specification):
 * 1) getManualWeldDataPreSignedUrlToUpload (chunked only if >100)
 * 2) PUT all files (parallel)
 */
export async function uploadBuckerarrestorTxtFilesToS3(
  options: BuckerarrestorS3UploadOptions
): Promise<BuckerarrestorS3UploadedFile[]> {
  const { files, projectId, weldId } = options;
  if (!files.length) return [];

  const safeWeld = String(weldId || 'UNKNOWN')
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, '_');

  type PresignRow = { presignedUrl: string; s3Key: string; fileName: string; file: File };
  const toUpload: PresignRow[] = [];

  for (let i = 0; i < files.length; i += PRESIGN_BATCH_SIZE) {
    const batch = files.slice(i, i + PRESIGN_BATCH_SIZE);
    const response = await axiosConfig.post(`pipeDocuments/getManualWeldDataPreSignedUrlToUpload`, {
      projectId,
      weldId: safeWeld || 'UNKNOWN',
      overwrite: true,
      files: batch.map((file) => ({
        fileName: file.name.replace(/\(\d+\)/g, '').trim(),
        contentType: file.type || 'text/plain',
        fileSize: file.size || 1,
      })),
    });
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Failed to get S3 upload URLs for Manual weld files');
    }

    const presignedData = Array.isArray(response.data?.data?.files)
      ? response.data.data.files
      : Array.isArray(response.data?.files)
        ? response.data.files
        : [];

    for (const data of presignedData) {
      const fileName = String(data.fileName || '').trim();
      const file =
        batch.find((f) => f.name.replace(/\(\d+\)/g, '').trim() === fileName) ||
        batch.find((f) => f.name === fileName);
      if (!file || !data.presignedUrl) {
        throw new Error(`Missing presigned URL for ${fileName || 'file'}`);
      }
      toUpload.push({
        presignedUrl: data.presignedUrl,
        s3Key: data.s3Key,
        fileName: data.fileName,
        file,
      });
    }
  }

  return mapPool(toUpload, PUT_CONCURRENCY, async (row) => {
    const uploadResponse = await axios.put(row.presignedUrl, row.file, {
      headers: {
        'Content-Type': row.file.type || 'text/plain',
      },
    });
    if (uploadResponse.status < 200 || uploadResponse.status >= 300) {
      throw new Error(`Failed to upload ${row.file.name} to S3`);
    }
    return { s3Key: row.s3Key, fileName: row.fileName } as BuckerarrestorS3UploadedFile;
  });
}

/** Build File objects from loaded txt content (zip entries have no original File). */
export function buckerarrestorLoadedToUploadFiles(
  loaded: Array<{ fileName: string; content: string }>
): File[] {
  return loaded.map((item) => {
    const blob = new Blob([item.content], { type: 'text/plain' });
    return new File([blob], item.fileName, { type: 'text/plain' });
  });
}
